/*
 * Purpose: Mission activity trail while artifactState === mission
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Route } from 'lucide-react';
import type { MissionStep } from '@/services/autoblocks/missionTypes';
import type { ComposeMode } from '@/services/autoblocks/missionTypes';
import { composeModeLabel } from '@/services/autoblocks/flowMode';

interface MissionTrailProps {
  steps: MissionStep[];
  composeMode?: ComposeMode | null;
  className?: string;
}

const KIND_DOT: Record<MissionStep['kind'], string> = {
  capture: 'bg-muted-foreground',
  interpret: 'bg-blue-500',
  confirm: 'bg-amber-500',
  mode_switch: 'bg-violet-500',
  plan: 'bg-primary',
  dry_run: 'bg-cyan-500',
  approve: 'bg-green-500',
  blocker: 'bg-destructive',
  express: 'bg-orange-500',
};

export function MissionTrail({ steps, composeMode, className }: MissionTrailProps) {
  if (steps.length === 0) return null;

  return (
    <Card className={cn('border-dashed', className)}>
      <CardHeader className="py-3 pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Route className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-semibold">Mission trail</CardTitle>
          </div>
          {composeMode && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {composeModeLabel(composeMode)} mode
            </span>
          )}
        </div>
        <CardDescription className="text-xs">
          If it&apos;s running, it&apos;s a Mission — steps may change until your Flow is validated.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 pb-3">
        <ScrollArea className="h-[min(140px,22vh)]">
          <ol className="space-y-2 pr-3">
            {steps.map((step) => (
              <li key={step.id} className="flex gap-2 text-xs">
                <span
                  className={cn('mt-1.5 h-2 w-2 rounded-full shrink-0', KIND_DOT[step.kind])}
                  aria-hidden
                />
                <div className="min-w-0">
                  <p className="font-medium text-foreground/90">{step.label}</p>
                  {step.detail && (
                    <p className="text-muted-foreground truncate">{step.detail}</p>
                  )}
                  <time className="text-[10px] text-muted-foreground/70">
                    {new Date(step.at).toLocaleTimeString()}
                  </time>
                </div>
              </li>
            ))}
          </ol>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
